import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import * as springroll from "springroll";
import {GoogleAnalytics} from "springroll-google-analytics-plugin";

import * as CaptionData from "json/close-caption.json";
import * as Constants from "Data/Constants";
import * as SFX from "Data/SoundEffect";
import * as VO from "Data/VoiceOver";
import * as Topics from "Data/Topics";
import Settings from "Data/Settings";

import { DuckSwapGame } from "States/DuckSwapGame";
import { FlyingGame } from "States/FlyingGame";
import { FreePlayMenu } from "States/FreePlayMenu";
import { Intro } from "States/Intro";
import { LoadScreen } from "States/LoadScreen";
import { MainMenu } from "States/MainMenu";
import { RestStopGame } from "States/RestStopGame";
import { ResultsScreen } from "States/ResultsScreen";
import { StartScreen } from "States/StartScreen";

import RestStopCompletePopup from "Popups/RestStopCompletePopup";
import SettingsPopup from "Popups/SettingsPopup";

import { CaptionManager } from "GameObjects/CaptionManager";
import { DefaultCaptionRenderer } from "GameObjects/DefaultCaptionRenderer";
import { DwellCursor } from "GameObjects/UX/DwellCursor";
import { KeyboardFocus } from "GameObjects/UX/KeyboardFocus";

import BadgePopup from "Popups/BadgePopup";
import RestStopFreePlayCompletePopup from "Popups/RestStopFreePlayCompletePopup";
import { SplashScreen } from "States/SplashScreen";

export class Application extends HLF.Application {
    protected captionManager: CaptionManager;
    protected blackAndWhiteFilter: PIXI.filters.ColorMatrixFilter;

    private _springroll?: springroll.Application;
    private _paused: boolean = false;

    private constructor() {
        super({
            transparent: false,
            backgroundColor: 0x7ab57b,
            preserveDrawingBuffer: (
                PIXI.utils.isMobile.android.device ||
                PIXI.utils.isMobile.amazon.device
            ) ? true : undefined,
        });
        PIXI.settings.FILTER_RESOLUTION = this.renderer.resolution;
        this.captionManager = new CaptionManager((CaptionData as any).default);
        this._webEventsManager.registerVisibilityChangedCallback((pVisible) => {
            this.onPause(!pVisible, "document.visibilitychange");
        });
        PubSub.subscribe(Topics.SETTINGS_CHANGED, this.onSettingsChanged.bind(this));
        PubSub.subscribe(Topics.PAUSE_GAME, (pTopic:string, pData?:string)=>this.onPause(true, pData));
        PubSub.subscribe(Topics.UNPAUSE_GAME, (pTopic:string, pData?:string)=>this.onPause(false, pData));

        this.blackAndWhiteFilter = new PIXI.filters.ColorMatrixFilter();
        this.blackAndWhiteFilter.blackAndWhite(false);
        this.blackAndWhiteFilter.enabled = false;

        this.stage.filters = [this.blackAndWhiteFilter];
    }

    static get instance(): Application {
        if (HLF.Application._instance === undefined) {
            HLF.Application._instance = new Application();
        }
        return HLF.Application.instance as Application;
    }

    public get captions() { return this.captionManager; }

    public get paused() { return this._paused; }

    public get analytics() {
        return {
            event: (action: string, label?: string, value?: number) => {
                return (this._springroll?.getPlugin("GoogleAnalytics") as (GoogleAnalytics & springroll.ApplicationPlugin)).event("Duck Dash", action, label, value);
            },
        }
    }

    public get requiredAssets(): HLF.AssetMapData[] { // For everything _except_ splash screen
        const assets = new Array<HLF.AssetMapData>();

        // settings and main menu
        assets.push(...HLF.AssetFactory(
            { "@1x": ["1"], "@2x": ["1", "2", "3", "4"] },
            (i, r) => new HLF.TextureAtlasAsset("common-" + i),
        ));

        assets.push(new HLF.TextureAsset("empty-pixel", HLF.AssetType.PNG));
        assets.push(new HLF.TextureAsset("black2x2", HLF.AssetType.PNG));
        assets.push(new HLF.FontAsset(Constants.FONT_HELVETICA));
        assets.push(new HLF.FontAsset(Constants.FONT_RIFFICFREE_BOLD_36));
        assets.push(new HLF.TextureAsset("cyberchase-splashscreen-bg", HLF.AssetType.JPG));

        return assets;
    }

    public init(): void {
        this._stateManager.debug = false;
        this._loadManager.debug = false;
        this._popupManager.debug = false;
        this._voiceoverManager.debug = false;

        this._keyboardManager.debug = false;
        this._keyboardManager.removeKeyBinding(HLF.Input.Direction.UP, HLF.Input.KeyCodes.UP);
        this._keyboardManager.removeKeyBinding(HLF.Input.Direction.DOWN,  HLF.Input.KeyCodes.DOWN);
        this._keyboardManager.removeKeyBinding(HLF.Input.Direction.UP, HLF.Input.KeyCodes.W);
        this._keyboardManager.removeKeyBinding(HLF.Input.Direction.DOWN,  HLF.Input.KeyCodes.S);
        this._keyboardManager.removeKeyBinding("Enter", HLF.Input.KeyCodes.SPACEBAR);
        this._keyboardManager.addKeyBinding(HLF.Input.Direction.FORWARDS, HLF.Input.KeyCodes.SPACEBAR);
        this._keyboardManager.addKeyBinding(HLF.Input.Direction.FORWARDS, HLF.Input.KeyCodes.D);
        this._keyboardManager.addKeyBinding(HLF.Input.Direction.FORWARDS, HLF.Input.KeyCodes.RIGHT);
        this._keyboardManager.addKeyBinding(HLF.Input.Direction.BACKWARDS, HLF.Input.KeyCodes.SPACEBAR, {shiftKey: true});
        this._keyboardManager.addKeyBinding(HLF.Input.Direction.BACKWARDS, HLF.Input.KeyCodes.A);
        this._keyboardManager.addKeyBinding(HLF.Input.Direction.BACKWARDS, HLF.Input.KeyCodes.LEFT);
        this._keyboardManager.printAllKeyBindings();

        this.resizer.sizeMin = new PIXI.Point(1024, 768);
        this.resizer.sizeMax = new PIXI.Point(1664, 768);

        super.init();

        this.initSpringroll();

        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
    }

    public update() {
        super.update();
        const deltaTime: number = PIXI.Ticker.shared.elapsedMS / 1000;
        if (!this.paused || SettingsPopup.isOpen) {
            this.captionManager?.update(deltaTime);
        }
    }

    protected createSplashScreen() {
        return new SplashScreen();
    }

    protected createAssetMap(): void {
        // Set up map
        HLF.AssetMap.addAssetGroup(HLF.SplashScreen.NAME, [
            new HLF.TextureAsset("cyberchase-splashscreen-bg", HLF.AssetType.JPG),

            ...HLF.AssetFactory(
                { "@1x": ["1"], "@2x": ["1", "2", "3", "4"] },
                (i, r) => new HLF.TextureAtlasAsset("common-" + i),
            ),
        ]);

        HLF.AssetMap.addAssetGroup(MainMenu.NAME, [
            new HLF.TextureAtlasAsset("splash-1"),
            new HLF.AudioAsset("flyingloop", HLF.AudioCategory.BGM),
        ]);

        HLF.AssetMap.addAssetGroup(FreePlayMenu.NAME, [
            new HLF.AudioAsset("flyingloop", HLF.AudioCategory.BGM),
        ]);

        HLF.AssetMap.addAssetGroup(StartScreen.NAME, [
            new HLF.TextureAtlasAsset("splash-1"),
            new HLF.AudioAsset("flyingloop", HLF.AudioCategory.BGM),
            new HLF.AudioAsset(SFX.UI_CLICK, HLF.AudioCategory.SFX),
            new HLF.AudioAsset(VO.TITLE, HLF.AudioCategory.VO),
            new HLF.AudioAsset(VO.PROMPT_AUDIO_DESCRIPTION, HLF.AudioCategory.VO),
        ]);

        HLF.AssetMap.addAssetGroup(Intro.NAME, [
            new HLF.TextureAtlasAsset("intro-1"),
            new HLF.TextureAsset("cyberchase-intro-bg", HLF.AssetType.JPG),
            // intro now includes flying ducks
            ...HLF.AssetFactory(
                { "@1x": ["1"], "@2x": ["1", "2"] },
                (i, r) => new HLF.TextureAtlasAsset("flying-" + i),
            ),
            // intro now includes progress bar
            new HLF.TextureAsset("cyberchase-flyinggame-progress-bar-full", HLF.AssetType.PNG),
            new HLF.AudioAsset("flyingloop", HLF.AudioCategory.BGM),
        ]);

        HLF.AssetMap.addAssetGroup(FlyingGame.NAME, [
            ...HLF.AssetFactory(
                { "@1x": ["1"], "@2x": ["1", "2"] },
                (i, r) => new HLF.TextureAtlasAsset("flying-" + i),
            ),
            new HLF.TextureAsset("Cyberchase_PanningBGTiled_L", "images/static/Cyberchase_PanningBGTiled_L.png"), // full path because we only use @1x for this
            new HLF.TextureAsset("Cyberchase_PanningBGTiled_R", "images/static/Cyberchase_PanningBGTiled_R.png"), // full path because we only use @1x for this
            new HLF.TextureAsset("cyberchase-flyinggame-flight-lanes", HLF.AssetType.PNG),
            new HLF.TextureAsset("cyberchase-flyinggame-progress-bar-full", HLF.AssetType.PNG),
            new HLF.AudioAsset("quack_lane_1", HLF.AudioCategory.SFX),
            new HLF.AudioAsset("quack_lane_2", HLF.AudioCategory.SFX),
            new HLF.AudioAsset("quack_lane_3", HLF.AudioCategory.SFX),
            new HLF.AudioAsset(SFX.COLLISION, HLF.AudioCategory.SFX),
            new HLF.AudioAsset("flyingloop", HLF.AudioCategory.BGM),
            new HLF.AudioAsset(SFX.OBSTACLE_GULL, HLF.AudioCategory.SFX),
            new HLF.AudioAsset(SFX.OBSTACLE_WIND, HLF.AudioCategory.SFX),
            new HLF.AudioAsset(SFX.WING_FLAPPING, HLF.AudioCategory.SFX),
        ]);

        HLF.AssetMap.addAssetGroup(DuckSwapGame.NAME, [
            new HLF.TextureAtlasAsset("swap-1"),
            new HLF.TextureAsset("cyberchase-duckswap-bg", HLF.AssetType.PNG),
            new HLF.TextureAsset("cyberchase-duckswap-duckname-container", HLF.AssetType.PNG),
            new HLF.AudioAsset("duckswaploop_2", HLF.AudioCategory.BGM),
            new HLF.AudioAsset(SFX.GOOD_PICK, HLF.AudioCategory.SFX),
            new HLF.AudioAsset(SFX.BAD_PICK, HLF.AudioCategory.SFX),
        ]);

        HLF.AssetMap.addAssetGroup(RestStopGame.NAME, [
            ...HLF.AssetFactory(
                { "@1x": ["1"], "@2x": ["1", "2"] },
                (i, r) => new HLF.TextureAtlasAsset("reststop-" + i),
            ),
            new HLF.TextureAtlasAsset("swap-1"), // rest stop popup uses duck swap assets
            new HLF.TextureAsset("cyberchase-duckswap-duckname-container", HLF.AssetType.PNG), // rest stop popup uses duck swap assets
            new HLF.TextureAsset("cyberchase-reststop-bg", HLF.AssetType.PNG),
            new HLF.TextureAsset("cyberchase-reststop-bargraph", HLF.AssetType.PNG),
            new HLF.TextureAsset("cyberchase-reststop-bargraph-stressors", HLF.AssetType.PNG),
            new HLF.TextureAsset("cyberchase-reststop-compare-bargraph-resource", HLF.AssetType.PNG),
            new HLF.TextureAsset("cyberchase-reststop-compare-bargraph-stressor", HLF.AssetType.PNG),
            new HLF.TextureAsset("cyberchase-reststop-comp-pondtitlebg", HLF.AssetType.PNG),
            new HLF.TextureAsset("cyberchase-reststop-comp-title-bg", HLF.AssetType.PNG),
            new HLF.AudioAsset("reststoploop", HLF.AudioCategory.BGM),
            new HLF.AudioAsset(SFX.GOOD_PICK, HLF.AudioCategory.SFX),
            new HLF.AudioAsset(SFX.BAD_PICK, HLF.AudioCategory.SFX),
            new HLF.AudioAsset(SFX.TALLY, HLF.AudioCategory.SFX),
            new HLF.AudioAsset(SFX.ENERGY_REFILL, HLF.AudioCategory.SFX),
        ]);

        HLF.AssetMap.addAssetGroup(ResultsScreen.NAME, [
            new HLF.TextureAsset("cyberchase-results-bg", HLF.AssetType.JPG),
            new HLF.TextureAsset("cyberchase-results-banner", HLF.AssetType.PNG),
            new HLF.TextureAtlasAsset("results-1"),
            new HLF.AudioAsset("flyingloop", HLF.AudioCategory.BGM),
            new HLF.AudioAsset(SFX.FANFARE, HLF.AudioCategory.SFX),
            new HLF.AudioAsset(SFX.BADGE, HLF.AudioCategory.SFX),
        ]);
    }

    protected registerLoadingScreens(): void {
        this._loadManager.registerLoadScreen(LoadScreen.NAME, new LoadScreen());
    }

    protected registerPopups(): void {
        this._popupManager.registerPopup(() => new SettingsPopup(), SettingsPopup.NAME);
        this._popupManager.registerPopup(() => new RestStopCompletePopup(), RestStopCompletePopup.NAME);
        this._popupManager.registerPopup(() => new RestStopFreePlayCompletePopup(), RestStopFreePlayCompletePopup.NAME);
        this._popupManager.registerPopup(() => new BadgePopup(), BadgePopup.NAME);
    }

    protected registerStates(): void {
        this._stateManager.registerState(StartScreen.NAME, () => new StartScreen());
        this._stateManager.registerState(Intro.NAME, () => new Intro());
        this._stateManager.registerState(FlyingGame.NAME, () => new FlyingGame());
        this._stateManager.registerState(DuckSwapGame.NAME, () => new DuckSwapGame());
        this._stateManager.registerState(RestStopGame.NAME, () => new RestStopGame());
        this._stateManager.registerState(ResultsScreen.NAME, () => new ResultsScreen());
        this._stateManager.registerState(MainMenu.NAME, () => new MainMenu());
        this._stateManager.registerState(FreePlayMenu.NAME, () => new FreePlayMenu());
    }

    protected onLoadRequiredAssetsComplete(): void {
        super.onLoadRequiredAssetsComplete();

        this.stage.addChild(new HLF.Input.KeyboardFocusManager(KeyboardFocus));
        // PubSub.publishSync(HLF.SET_KEYBOARD_ENABLED, false);

        this.stage.addChild(this.captionManager);
        this.captionManager.setRenderer(new DefaultCaptionRenderer(Constants.FONT_HELVETICA, 36 * HLF.Application.instance.renderer.resolution));
        this.captionManager.onResize(this._size);
        if (this._springroll) {
            Settings.closedCaptionsEnabled = !this._springroll.state.captionsMuted.value;
        }

        this.stage.addChild(new DwellCursor());
        PubSub.subscribe(Topics.DWELL_BEGIN, () => PubSub.publishSync(HLF.CLEAR_FOCUS));

        if (Constants.DEBUG_DEFAULT_STATE === undefined) {
            PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
                {id: StartScreen.NAME, data: {name: StartScreen.NAME}}, SplashScreen.NAME, ...HLF.StateToken.TRANSITION_FIRST_VIEW,
            ));
        }
        else {
            PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
                {id: Constants.DEBUG_DEFAULT_STATE, data: {name: Constants.DEBUG_DEFAULT_STATE}}, HLF.SplashScreen.NAME, ...HLF.StateToken.TRANSITION_FIRST_VIEW,
            ));
        }
    }

    protected onSettingsChanged(pTopic: typeof Topics.SETTINGS_CHANGED, pSettings: Readonly<typeof Settings>): void {
        this.captions.active = pSettings.closedCaptionsEnabled;
        this.audio.masterVolume = pSettings.masterVolume;
        this.audio.setCategoryVolume(HLF.AudioCategory.SFX, pSettings.soundEffectsVolume * Constants.SFX_VOLUME_MAX);
        this.audio.setCategoryVolume(HLF.AudioCategory.BGM, pSettings.musicVolume * Constants.BGM_VOLUME_MAX);
        this.audio.setCategoryVolume(HLF.AudioCategory.VO, pSettings.voiceoverVolume * Constants.VO_VOLUME_MAX);
        this.blackAndWhiteFilter.enabled = pSettings.colorBlindMode === 1;
    }

    private initSpringroll(): void {
        this._springroll = new springroll.Application({
            features: {
                captions: true,
                sound: true,
                vo: true,
                music: true,
                sfx: true,
            },
        });

        springroll.Application.uses(new GoogleAnalytics());

        this._springroll.state.ready.subscribe(() => {
            this.onSpringrollInitialized();
        });

        this._springroll.state.pause.subscribe((isPaused: boolean) => {
            this.onPause(isPaused, "springroll");
        });

        this._springroll.state.playOptions.subscribe((playOptions: object) => {
            console.log("New playOptions value set to", playOptions);
        });

        this._springroll.state.captionsMuted.subscribe((result: boolean) => {
            Settings.closedCaptionsEnabled = !result;
        });

        this._springroll.state.soundVolume.subscribe((result: number) => {
            Settings.masterVolume = result;
        });

        this._springroll.state.musicVolume.subscribe((result: number) => {
            Settings.musicVolume = result;
        });

        this._springroll.state.voVolume.subscribe((result: number) => {
            Settings.voiceoverVolume = result;
        });

        this._springroll.state.sfxVolume.subscribe((result: number) => {
            Settings.soundEffectsVolume = result;
        });
    }

    private onSpringrollInitialized(): void {
        this.analytics.event("startGame");
        window.addEventListener("beforeunload", (event) => {
            this.analytics.event("endGame");
        });
    }

    private onPause(pIsPaused: boolean, pReason?: string): void {
        const wasPaused = this._paused;

        if (SettingsPopup.isOpen && wasPaused && !pIsPaused) {
            HLF.LogUtils.logWarning("onPause: Ignoring unpause from " + pReason + " because Settings Popup is open", {className: "Application", color: HLF.LogUtils.COLOR_RELISH});
            return;
        }

        if (pIsPaused && !wasPaused) {
            HLF.LogUtils.log("onPause: Paused by " + pReason, {className: "Application", color: HLF.LogUtils.COLOR_RELISH});
            PIXI.Ticker.shared.stop(); // this also pauses any asset loading
            PubSub.publishSync(HLF.GAME_PAUSED, undefined);
            // NOTE: individual scenes handle their own audio muting
        } else if (wasPaused && !pIsPaused) {
            HLF.LogUtils.log("onPause: Unpaused by " + pReason, {className: "Application", color: HLF.LogUtils.COLOR_RELISH});
            PIXI.Ticker.shared.start();
            PubSub.publishSync(HLF.GAME_UNPAUSED, undefined);
            // NOTE: individual scenes handle their own audio unmuting
        }
        else {
            HLF.LogUtils.logWarning("onPause: Ignoring " + (pIsPaused ? "pause" : "unpause") + " from " + pReason + " because game is already " + (pIsPaused ? "paused" : "unpaused"), {className: "Application", color: HLF.LogUtils.COLOR_RELISH});
        }
        this._paused = pIsPaused;
    }

}
